import React, { useEffect, useReducer } from 'react';
import { GetListCases } from 'src/constants/ApiConstants';
import { GetApiBaseUrl } from 'src/constants/UrlConstants';
import {
  IGetCaseListItem,
  IGetCaseListRequest,
  IGetCaseListResponse,
} from 'src/state/slice/models/CaseLifecycleTypes';
import { fetchWithRetryAndCaptureMetrics } from 'src/utilities/ApiHelper';
import {
  apiStateReducerFunction,
  handleApiFailure,
  handleApiRefetching,
  handleApiSuccess,
} from './api-reducer';
import { logger } from 'src/logger';

/**
 * Function to fetch data from the backend and accumulate it based on pages.
 * @param caseRequest IGetCaseListRequest
 * @returns list of caseRequest.
 */
async function fetchAPIRecords(caseRequest: IGetCaseListRequest) {
  let nextToken = '';
  let accumulatedData: Array<IGetCaseListItem> = [];
  do {
    try {
      const response = await fetchWithRetryAndCaptureMetrics(
        GetListCases.metricsPublisher,
        `${GetApiBaseUrl()}${GetListCases.getRoute()}`,
        {
          method: GetListCases.method,
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ...caseRequest, nextToken }),
        },
      );
      if (response.error) {
        return Promise.reject(response.error);
      }
      const data: IGetCaseListResponse = response.body;
      accumulatedData = accumulatedData.concat(data.caseDetails);
      nextToken = data.nextToken;
    } catch (error) {
      return Promise.reject(error);
    }
  } while (nextToken); //start and end value of next token
  return Promise.resolve(accumulatedData);
}

export interface ApiResponseFetcher {
  isLoading: boolean;
  isError: boolean;
  error: any;
  data: IGetCaseListResponse | undefined;
  refetch: Function;
  isFetching: boolean;
}
/**
 * This hook will be used to manage the lifecycle of an API request to fetch & accumulate cases.
 * @param caseRequest
 * @returns
 */
export const useGetCaseListAllRecords = (
  caseRequest: IGetCaseListRequest,
): ApiResponseFetcher => {
  const [state, dispatch] = useReducer(apiStateReducerFunction, {
    isLoading: true,
    isFetching: false,
    request: caseRequest,
    isError: false,
    error: undefined,
    aggregatedResponse: undefined,
  });

  useEffect(() => {
    if (state.isLoading || state.isFetching) {
      fetchAPIRecords(state.request)
        .then((data: Array<IGetCaseListItem>) => {
          handleApiSuccess(dispatch, data);
        })
        .catch((error) => {
          logger.error(
            `Error while calling useGetCaseListAllRecords ${JSON.stringify(
              error,
            )}`,
          );
          handleApiFailure(dispatch, error);
        });
    }
  }, [state.isLoading, state.isFetching]);

  const refetch = (caseRequest: IGetCaseListRequest) => {
    handleApiRefetching(dispatch, caseRequest);
  };

  return {
    isLoading: state.isLoading,
    isError: !!state.error,
    error: state.error,
    data: state.aggregatedResponse,
    refetch,
    isFetching: state.isFetching,
  } as ApiResponseFetcher;
};
